import { type EntryIdInput, formatEntryId } from "./entryId";
import { createAction, type MutationAction } from "./mutationAction";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	apiCall,
	plural,
	refreshEntries,
} from "./types";

/**
 * Bulk delete via the v2 atomic endpoint.
 *
 *   POST /v2/entries/delete { ids: ["ent_<n>", …] }
 *
 * Server-side this runs every soft-delete inside one Prisma `$transaction`,
 * so any failure (404 / 403 ENTRY_EXPORTED) rolls back the whole batch. The
 * Optimistic update strips every id from the entries cache; on failure the rollback
 * restores the snapshot. The post-action refresh reconciles to the server's
 * actual state.
 */
export function deleteAction(
	ids: EntryIdInput[],
	deps: ActionDeps,
	timekeeperId?: string | null,
): MutationAction {
	const wireIds = ids.map(formatEntryId);
	const v2IdSet = new Set(wireIds);
	const rollback = deps.updateEntries((prev) =>
		prev.filter((e) => !v2IdSet.has(e.id)),
	);
	const n = ids.length;

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: "/v2/entries/delete",
				method: "POST",
				body: { ids: wireIds },
				headers: actingHeaders(timekeeperId),
			}),
		rollback,
		toast: {
			loading: `Deleting ${n} ${plural(n)}…`,
			success: `Deleted ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Some entries are exported and can't be deleted";
					if (err.code === "ENTRY_NOT_FOUND")
						return "One or more entries no longer exist";
				}
				return "Failed to delete entries";
			},
		},
		onSuccess: () => refreshEntries(deps),
		onError: () => refreshEntries(deps),
	});
}
